import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useTranslation } from 'react-i18next';
import { BUNDLE_CONFIG } from '../config/bundle';

interface BundleOfferBannerProps {
  onClose?: () => void;
  onPurchase?: () => void;
}

export const BundleOfferBanner: React.FC<BundleOfferBannerProps> = ({
  onClose,
  onPurchase
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(BUNDLE_CONFIG.enabled);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  const handlePurchase = () => {
    onPurchase?.();
  };

  // Don't render if disabled
  if (!BUNDLE_CONFIG.enabled || !isVisible) {
    return null;
  }

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ duration: 0.5, type: "spring", damping: 25 }}
      className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-orange-500 via-orange-600 to-red-500 border-t-4 border-yellow-300 shadow-2xl"
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Content */}
          <div className="flex-1 flex flex-col md:flex-row items-center gap-4">
            {/* Left: Bundle Info */}
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-full">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-white text-black text-xs px-2 py-1 animate-pulse">
                    {t("bundle.limitedOffer")}
                  </Badge>
                  <span className="text-white/60 text-sm line-through">
                    {t("bundle.originalPrice")}
                  </span>
                  <span className="text-white text-xl font-bold">
                    {t("bundle.offerPrice")}
                  </span>
                </div>
                <div className="text-white text-sm md:text-base">
                  <span className="font-semibold">{t("bundle.title")}:</span> {t("bundle.description")}
                </div>
              </div>
            </div>

            {/* Center: Savings */}
            <div className="text-center md:px-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                <span className="text-white text-lg font-bold">
                  {t("bundle.savings")}
                </span>
              </div>
            </div>

            {/* Right: CTA Button */}
            <div className="flex-shrink-0">
              <Button
                size="lg"
                onClick={handlePurchase}
                className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-8 py-3 rounded-full font-bold shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                {t("bundle.cta")}
              </Button>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="text-center mt-2">
          <p className="text-white/90 text-xs">
            {t("bundle.additionalInfo")}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default BundleOfferBanner;
